import { useEffect, useState } from "react";
import { NavLink, Outlet, useNavigate, useLocation } from "react-router-dom";
import { useAuth } from "../context/AuthContext";
import api from "../lib/api";
import { subscribePush } from "../lib/push";
import { flushQueue, queueSize } from "../lib/offlineQueue";
import {
  LayoutDashboard, BookOpen, Users, ShoppingCart, HardHat, Camera,
  FileText, Stamp, Smartphone, Boxes, Map, Settings, LogOut, Menu, X, Building2,
  CheckSquare, CalendarDays, Bell, MessageSquare, Megaphone, WifiOff, Home, UserCircle, Wallet, ClipboardList,
  FileCheck, Activity, ShieldCheck, Ticket, CreditCard
} from "lucide-react";

const NAV = [
  { to: "/", label: "Dashboard", icon: LayoutDashboard, module: "dashboard" },
  { to: "/penjualan", label: "Penjualan & Siteplan", icon: Map, module: "penjualan" },
  { to: "/crm", label: "CRM Pipeline", icon: Users, module: "crm" },
  { to: "/sales", label: "Sales Mobile", icon: Smartphone, module: "sales" },
  { to: "/todo", label: "To Do List", icon: CheckSquare, module: "todo", taskBadge: true },
  { to: "/kalender", label: "Kalender", icon: CalendarDays, module: "kalender" },
  { to: "/chat", label: "Chat", icon: MessageSquare, module: "chat", always: true },
  { to: "/akuntansi", label: "Akuntansi", icon: BookOpen, module: "akuntansi" },
  { to: "/purchase", label: "Purchase", icon: ShoppingCart, module: "purchase" },
  { to: "/inventory", label: "Inventory", icon: Boxes, module: "inventory" },
  { to: "/proyek", label: "Proyek", icon: HardHat, module: "proyek" },
  { to: "/absensi", label: "Absensi", icon: Camera, module: "absensi" },
  { to: "/cuti", label: "Cuti", icon: CalendarDays, module: "cuti" },
  { to: "/monitoring-tugas", label: "Monitoring Tugas", icon: ClipboardList, module: "todo" },
  { to: "/konsumen", label: "Data Konsumen", icon: UserCircle, module: "dashboard" },
  { to: "/verifikasi-dokumen", label: "Verifikasi Dok App", icon: FileCheck, module: "penjualan" },
  { to: "/aktivitas-app", label: "Aktivitas App", icon: Activity, module: "crm" },
  { to: "/garansi", label: "Garansi", icon: ShieldCheck, module: "penjualan" },
  { to: "/tiket", label: "Tiket Aplikasi", icon: Ticket, module: "penjualan" },
  { to: "/referral-sales", label: "Kode Referral", icon: Ticket, module: "crm" },
  { to: "/promo-voucher", label: "Promo & Voucher", icon: CreditCard, module: "crm" },
  { to: "/pembayaran-app", label: "Pembayaran App", icon: CreditCard, module: "akuntansi" },
  { to: "/dokumen", label: "Dokumen", icon: FileText, module: "dokumen" },
  { to: "/approvals", label: "Approve & Sign", icon: Stamp, module: "approval", badge: true },
  { to: "/pengajuan", label: "Pengajuan Bayar", icon: Wallet, module: "pengajuan" },
  { to: "/profile", label: "Profil Saya", icon: UserCircle, always: true },
  { to: "/settings", label: "Pengaturan & RBAC", icon: Settings, module: "settings" },
];

export default function Layout() {
  const { user, logout, hasPerm, projects, projectId, selectProject } = useAuth();
  const navigate = useNavigate();
  const [open, setOpen] = useState(false);
  const [pendingCount, setPendingCount] = useState(0);
  const [taskCount, setTaskCount] = useState(0);
  const location = useLocation();

  useEffect(() => {
    if (user && !["Super Admin", "Konsumen"].includes(user.role)) {
      api.get("/attendance/my-status").then((r) => {
        if (r.data.profile_incomplete) {
          if (location.pathname !== "/profile") navigate("/profile");
          return;
        }
        if (!r.data.off_day && !r.data.checked_in && location.pathname !== "/absensi") navigate("/absensi");
      }).catch(() => {});
    }
  }, [user, location.pathname]);

  const [notifs, setNotifs] = useState([]);
  const [notifOpen, setNotifOpen] = useState(false);
  const unread = notifs.filter((n) => !n.read).length;
  useEffect(() => {
    if (!user) return;
    subscribePush();
    const loadN = () => api.get("/notifications").then((r) => setNotifs(r.data)).catch(() => {});
    loadN();
    const t = setInterval(loadN, 5000);
    return () => clearInterval(t);
  }, [user]);

  useEffect(() => {
    if (hasPerm("approval", "view")) {
      api.get("/approvals").then((r) => {
        setPendingCount(r.data.filter((a) => a.status === "pending" && a.needs_my_action).length);
      }).catch(() => {});
    }
    if (hasPerm("todo", "view")) {
      api.get("/tasks").then((r) => {
        setTaskCount(r.data.filter((t) => t.status === "todo").length);
      }).catch(() => {});
    }
  }, [hasPerm]);

  const doLogout = () => {
    logout();
    navigate("/login");
  };

  const [annOpen, setAnnOpen] = useState(false);
  const [annForm, setAnnForm] = useState({ title: "", body: "" });
  const [online, setOnline] = useState(navigator.onLine);
  const [qCount, setQCount] = useState(0);

  useEffect(() => {
    const onOnline = () => { setOnline(true); flushQueue(); };
    const onOffline = () => setOnline(false);
    const onQueue = () => setQCount(queueSize());
    window.addEventListener("online", onOnline);
    window.addEventListener("offline", onOffline);
    window.addEventListener("offline-queue-change", onQueue);
    onQueue();
    return () => {
      window.removeEventListener("online", onOnline);
      window.removeEventListener("offline", onOffline);
      window.removeEventListener("offline-queue-change", onQueue);
    };
  }, []);
  const sendAnnouncement = async () => {
    if (!annForm.title.trim()) return;
    try {
      const r = await api.post("/announcements", annForm);
      setAnnOpen(false);
      setAnnForm({ title: "", body: "" });
      alert(r.data.message);
    } catch (e) { alert(e.response?.data?.detail || "Gagal mengirim pengumuman"); }
  };

  const links = NAV.filter((n) => n.always || n.module === "dashboard" || hasPerm(n.module, "view"));

  return (
    <div className="min-h-screen bg-slate-950 text-slate-100 flex" data-testid="app-layout">
      <aside
        className={`fixed inset-y-0 left-0 z-40 w-64 bg-slate-900 border-r border-slate-800 transform transition-transform duration-200 lg:translate-x-0 ${open ? "translate-x-0" : "-translate-x-full"}`}
        data-testid="sidebar"
      >
        <div className="flex items-center gap-3 px-5 h-16 border-b border-slate-800">
          <img src="/icons/icon-192.png" alt="Logo SPASI" className="w-9 h-9 rounded-lg" />
          <div>
            <div className="font-extrabold text-sm tracking-[0.2em] text-slate-200">SPASI</div>
            <div className="text-[10px] font-medium text-emerald-600">Satu Spasi, Semua Terkoneksi</div>
          </div>
        </div>
        <nav className="p-3 space-y-1 overflow-y-auto h-[calc(100%-4rem)]">
          {links.map((n) => (
            <NavLink
              key={n.to}
              to={n.to}
              end={n.to === "/"}
              onClick={() => setOpen(false)}
              data-testid={`sidebar-link-${n.module}`}
              className={({ isActive }) =>
                `flex items-center gap-3 px-3 py-2.5 rounded-lg text-sm transition-colors duration-150 border-l-2 ${
                  isActive
                    ? "bg-emerald-600/10 text-emerald-300 border-emerald-500"
                    : "text-slate-400 border-transparent hover:bg-slate-800 hover:text-slate-100"
                }`
              }
            >
              <n.icon className="w-4 h-4 shrink-0" />
              <span className="flex-1">{n.label}</span>
              {n.badge && pendingCount > 0 && (
                <span className="text-[10px] font-mono bg-amber-500/20 text-amber-300 border border-amber-500/40 rounded-full px-2 py-0.5" data-testid="pending-approval-badge">
                  {pendingCount}
                </span>
              )}
              {n.taskBadge && taskCount > 0 && (
                <span className="text-[10px] font-mono bg-cyan-500/20 text-cyan-300 border border-cyan-500/40 rounded-full px-2 py-0.5" data-testid="open-task-badge">
                  {taskCount}
                </span>
              )}
            </NavLink>
          ))}
        </nav>
      </aside>

      {open && <div className="fixed inset-0 bg-black/60 z-30 lg:hidden" onClick={() => setOpen(false)} />}

      <div className="flex-1 lg:ml-64 flex flex-col min-h-screen min-w-0">
        <header className="sticky top-0 z-20 h-16 bg-slate-900/95 backdrop-blur-md border-b border-slate-800 flex items-center gap-2 sm:gap-4 px-3 sm:px-6" data-testid="topbar">
          <button className="lg:hidden text-slate-400 hover:text-white" onClick={() => setOpen(!open)} data-testid="mobile-menu-button">
            {open ? <X className="w-5 h-5" /> : <Menu className="w-5 h-5" />}
          </button>
          <div className="flex-1" />
          <select
            value={projectId}
            onChange={(e) => { selectProject(e.target.value); window.location.reload(); }}
            data-testid="global-project-selector"
            className="bg-slate-950 border border-slate-700 rounded-lg px-2.5 py-1.5 text-xs text-slate-200 max-w-[140px] sm:max-w-[180px] focus:outline-none focus:border-emerald-500/60"
          >
            <option value="">Semua Proyek (Konsolidasi)</option>
            {projects.map((p) => <option key={p.id} value={p.id}>{p.name}</option>)}
          </select>
          {["Super Admin", "Direktur"].includes(user?.role) && (
            <button onClick={() => setAnnOpen(true)} data-testid="announce-button" title="Kirim Pengumuman" className="relative text-slate-400 hover:text-emerald-300 p-2 transition-colors">
              <Megaphone className="w-5 h-5" />
            </button>
          )}
          <div className="relative">
            <button onClick={() => setNotifOpen(!notifOpen)} data-testid="notif-bell" className="relative text-slate-400 hover:text-white p-2 transition-colors">
              <Bell className="w-5 h-5" />
              {unread > 0 && <span className="absolute -top-0.5 -right-0.5 text-[9px] font-mono bg-rose-500 text-white rounded-full px-1.5 py-0.5" data-testid="notif-badge">{unread}</span>}
            </button>
            {notifOpen && (
              <div className="absolute right-0 top-11 w-80 max-w-[85vw] bg-slate-900 border border-slate-700 rounded-xl shadow-2xl z-50" data-testid="notif-dropdown">
                <div className="flex items-center justify-between px-4 py-2.5 border-b border-slate-800">
                  <span className="text-xs font-mono uppercase tracking-wider text-slate-400">Notifikasi</span>
                  <button className="text-[11px] text-emerald-400 hover:text-emerald-300" onClick={async () => { await api.put("/notifications/read-all"); setNotifs(notifs.map((n) => ({ ...n, read: true }))); }} data-testid="notif-read-all">Tandai dibaca</button>
                </div>
                <div className="max-h-80 overflow-y-auto">
                  {notifs.length === 0 && <p className="text-slate-500 text-xs p-4 text-center">Belum ada notifikasi.</p>}
                  {notifs.map((n) => (
                    <button key={n.id} onClick={() => { api.put(`/notifications/${n.id}/read`); setNotifOpen(false); if (n.link) navigate(n.link); }}
                      className={`w-full text-left px-4 py-3 border-b border-slate-800/60 hover:bg-slate-800/60 transition-colors ${n.read ? "opacity-50" : ""}`} data-testid={`notif-item-${n.id}`}>
                      <div className="text-xs text-slate-200">{n.title}</div>
                      {n.body && <div className="text-[11px] text-slate-500 mt-0.5">{n.body}</div>}
                      <div className="text-[10px] font-mono text-slate-600 mt-1">{(n.created_at || "").slice(5, 16).replace("T", " ")}</div>
                    </button>
                  ))}
                </div>
              </div>
            )}
          </div>
          <div className="text-right hidden sm:block">
            <div className="text-sm font-medium text-slate-200" data-testid="topbar-user-name">{user?.name}</div>
            <div className="text-[11px] font-mono text-emerald-400" data-testid="topbar-user-role">{user?.role}</div>
          </div>
          <button
            onClick={doLogout}
            data-testid="logout-button"
            className="flex items-center gap-2 text-xs text-slate-400 hover:text-rose-300 border border-slate-700 hover:border-rose-500/50 rounded-lg px-2.5 sm:px-3 py-2 transition-colors"
          >
            <LogOut className="w-3.5 h-3.5" /> <span className="hidden sm:inline">Keluar</span>
          </button>
        </header>
        <main className="flex-1 p-3 pb-24 sm:p-6 lg:p-8">
          {!online && (
            <div className="mb-3 bg-amber-500/15 border border-amber-500/50 text-amber-300 text-xs rounded-lg px-3 py-2.5 flex items-center gap-2" data-testid="offline-banner">
              <WifiOff className="w-4 h-4 shrink-0" />
              <span>Mode offline — halaman bisa dibuka dari cache{qCount > 0 ? `, ${qCount} data menunggu dikirim` : ""}. Data otomatis tersinkron saat koneksi kembali.</span>
            </div>
          )}
          {online && qCount > 0 && (
            <button onClick={() => flushQueue()} className="mb-3 w-full bg-cyan-500/10 border border-cyan-500/40 text-cyan-300 text-xs rounded-lg px-3 py-2 text-left" data-testid="flush-queue-button">
              {qCount} data tersimpan offline — ketuk untuk kirim sekarang
            </button>
          )}
          <Outlet />
        </main>
        <nav className="sm:hidden fixed bottom-0 inset-x-0 z-40 bg-slate-900/95 backdrop-blur border-t border-slate-800" style={{ paddingBottom: "env(safe-area-inset-bottom)" }} data-testid="mobile-bottom-nav">
          <div className="grid grid-cols-5">
            {[
              { to: "/", icon: Home, label: "Beranda" },
              { to: "/absensi", icon: Camera, label: "Absen" },
              { to: "/todo", icon: CheckSquare, label: "To-Do", badge: taskCount },
              { to: "/crm", icon: Users, label: "CRM" },
              { to: "/chat", icon: MessageSquare, label: "Chat" },
            ].map((it) => (
              <NavLink key={it.to} to={it.to} end={it.to === "/"} data-testid={`bottomnav-${it.label.toLowerCase()}`}
                className={({ isActive }) => `flex flex-col items-center gap-0.5 py-2.5 transition-colors ${isActive ? "text-emerald-400" : "text-slate-500 hover:text-slate-300"}`}>
                <span className="relative">
                  <it.icon className="w-5 h-5" />
                  {it.badge > 0 && <span className="absolute -top-1.5 -right-2 bg-rose-500 text-white text-[9px] font-bold rounded-full min-w-[14px] h-3.5 px-0.5 flex items-center justify-center">{it.badge > 9 ? "9+" : it.badge}</span>}
                </span>
                <span className="text-[10px] font-medium">{it.label}</span>
              </NavLink>
            ))}
          </div>
        </nav>
      </div>
      {annOpen && (
        <div className="fixed inset-0 z-50 flex items-center justify-center bg-black/70 p-4" data-testid="announce-modal">
          <div className="bg-slate-900 border border-slate-700 rounded-xl w-full max-w-md p-5 space-y-3">
            <div className="text-sm font-medium text-slate-200 flex items-center gap-2"><Megaphone className="w-4 h-4 text-emerald-400" /> Kirim Pengumuman ke Semua User</div>
            <input className="w-full bg-slate-950 border border-slate-700 rounded-lg px-3 py-2 text-sm text-slate-200 focus:outline-none focus:border-emerald-500/60" placeholder="Judul pengumuman (wajib)..." value={annForm.title} onChange={(e) => setAnnForm({ ...annForm, title: e.target.value })} data-testid="announce-title-input" />
            <textarea className="w-full bg-slate-950 border border-slate-700 rounded-lg px-3 py-2 text-sm text-slate-200 focus:outline-none focus:border-emerald-500/60" rows={3} placeholder="Isi pengumuman..." value={annForm.body} onChange={(e) => setAnnForm({ ...annForm, body: e.target.value })} data-testid="announce-body-input" />
            <p className="text-[11px] text-slate-500">Terkirim ke notifikasi in-app, push notification, dan email seluruh user.</p>
            <div className="flex justify-end gap-2">
              <button onClick={() => setAnnOpen(false)} className="text-xs text-slate-400 hover:text-slate-200 border border-slate-700 rounded-lg px-3 py-2" data-testid="announce-cancel">Batal</button>
              <button onClick={sendAnnouncement} className="text-xs bg-emerald-600 hover:bg-emerald-500 text-white rounded-lg px-4 py-2 transition-colors" data-testid="announce-send-button">Kirim ke Semua User</button>
            </div>
          </div>
        </div>
      )}
    </div>
  );
}
